// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Relatorios/RelatoriosVendas.tsx
//  Relatórios de Vendas: Cliente, Produto, Forma de Pagamento, Evolução.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { Users, Package, CreditCard, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  useVendasClientesSintetico,
  useVendasProdutosSintetico,
  useVendasFormasPagamento,
  useEvolucaoVendas,
} from '@/features/relatorios/hooks/useRelatorios';
import { BotaoExportar } from '@/components/BotaoExportar';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const fmtNum = (v: number, dec = 2) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(v);

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
};
const anoInicio = () => `${new Date().getFullYear()}-01-01`;

function FiltrosPeriodo({
  dtInicio, dtFim, onChangeDtInicio, onChangeDtFim,
}: {
  dtInicio: string; dtFim: string;
  onChangeDtInicio: (v: string) => void;
  onChangeDtFim: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">De</span>
        <Input type="date" value={dtInicio} onChange={(e) => onChangeDtInicio(e.target.value)} className="w-40" />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Até</span>
        <Input type="date" value={dtFim} onChange={(e) => onChangeDtFim(e.target.value)} className="w-40" />
      </div>
    </div>
  );
}

const PAGE_SIZES = [10, 20, 50] as const;

function PaginacaoBar({
  pagina, setPagina, tamanhoPagina, setTamanhoPagina, total, totalPaginas,
}: {
  pagina: number; setPagina: (p: number) => void;
  tamanhoPagina: number; setTamanhoPagina: (t: number) => void;
  total: number; totalPaginas: number;
}) {
  const first = (pagina - 1) * tamanhoPagina + 1;
  const last  = Math.min(pagina * tamanhoPagina, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t text-sm text-muted-foreground">
      <span>{total === 0 ? 'Nenhum registro' : `${first}–${last} de ${total}`}</span>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap">Por página:</span>
          <Select value={String(tamanhoPagina)} onValueChange={(v) => { setTamanhoPagina(Number(v)); setPagina(1); }}>
            <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
            <SelectContent>{PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-2">Pág {pagina} / {totalPaginas}</span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(pagina + 1)} disabled={pagina >= totalPaginas}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Aba Vendas por Cliente ────────────────────────────────────────────────────

function TabClientes() {
  const [dtInicio, setDtInicio] = useState(daysAgo(30));
  const [dtFim,    setDtFim]    = useState(today());
  const [pagina,   setPagina]   = useState(1);
  const [tam,      setTam]      = useState(20);

  const { data, isLoading } = useVendasClientesSintetico({ dtInicio, dtFim, pagina, tamanhoPagina: tam });
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} onChangeDtInicio={(v) => { setDtInicio(v); setPagina(1); }} onChangeDtFim={(v) => { setDtFim(v); setPagina(1); }} />
        <BotaoExportar endpoint="/api/relatorios/vendas/clientes/exportar" params={exportParams} />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead className="text-right">Qtd Docs</TableHead>
              <TableHead className="text-right">Total Bruto</TableHead>
              <TableHead className="text-right">Desconto</TableHead>
              <TableHead className="text-right">Total Líquido</TableHead>
              <TableHead>Última Venda</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
            ) : !data?.itens.length ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.itens.map((r) => (
              <TableRow key={r.clienteId}>
                <TableCell className="font-medium">{r.nomeCliente}</TableCell>
                <TableCell className="text-right">{r.qtdDocumentos}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCur(r.totalBruto)}</TableCell>
                <TableCell className="text-right tabular-nums text-red-500">{r.totalDesconto > 0 ? `-${fmtCur(r.totalDesconto)}` : '—'}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalLiquido)}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{new Date(r.ultimaVenda).toLocaleDateString('pt-BR')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tamanhoPagina={tam} setTamanhoPagina={setTam} total={data.total} totalPaginas={data.totalPaginas} />}
    </div>
  );
}

// ── Aba Vendas por Produto ────────────────────────────────────────────────────

function TabProdutos() {
  const [dtInicio, setDtInicio] = useState(daysAgo(30));
  const [dtFim,    setDtFim]    = useState(today());
  const [pagina,   setPagina]   = useState(1);
  const [tam,      setTam]      = useState(20);

  const { data, isLoading } = useVendasProdutosSintetico({ dtInicio, dtFim, pagina, tamanhoPagina: tam });
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} onChangeDtInicio={(v) => { setDtInicio(v); setPagina(1); }} onChangeDtFim={(v) => { setDtFim(v); setPagina(1); }} />
        <BotaoExportar endpoint="/api/relatorios/vendas/produtos/exportar" params={exportParams} />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produto</TableHead>
              <TableHead className="text-right">Qtd Vendida</TableHead>
              <TableHead className="text-right">Valor Total</TableHead>
              <TableHead className="text-right">Desconto</TableHead>
              <TableHead className="text-right">Valor Líquido</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
            ) : !data?.itens.length ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.itens.map((r) => (
              <TableRow key={r.produtoId}>
                <TableCell className="font-medium">{r.descricao}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtNum(r.qtdTotal)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCur(r.valorTotal)}</TableCell>
                <TableCell className="text-right tabular-nums text-red-500">{r.valorDesconto > 0 ? `-${fmtCur(r.valorDesconto)}` : '—'}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.valorLiquido)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tamanhoPagina={tam} setTamanhoPagina={setTam} total={data.total} totalPaginas={data.totalPaginas} />}
    </div>
  );
}

// ── Aba Formas de Pagamento ───────────────────────────────────────────────────

function TabFormasPagamento() {
  const [dtInicio, setDtInicio] = useState(daysAgo(30));
  const [dtFim,    setDtFim]    = useState(today());

  const { data, isLoading } = useVendasFormasPagamento({ dtInicio, dtFim });
  const total = data?.reduce((s, x) => s + x.totalVendas, 0) ?? 0;
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} onChangeDtInicio={setDtInicio} onChangeDtFim={setDtFim} />
        <BotaoExportar endpoint="/api/relatorios/vendas/formas-pagamento/exportar" params={exportParams} />
      </div>
      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : !data?.length ? (
        <p className="text-muted-foreground text-center py-8">Nenhum dado no período.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-between text-sm text-muted-foreground pb-1 border-b">
            <span>Total do período: <span className="font-semibold text-foreground">{fmtCur(total)}</span></span>
            <span>{data.length} forma{data.length !== 1 ? 's' : ''}</span>
          </div>
          {data.map((r) => (
            <div key={r.formaPagRecId} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium">{r.descricaoFormaPag}</span>
                <div className="flex gap-4 tabular-nums">
                  <span className="text-muted-foreground">{r.qtdTransacoes} transações</span>
                  <span>{fmtNum(r.percentual)}%</span>
                  <span className="font-semibold">{fmtCur(r.totalVendas)}</span>
                </div>
              </div>
              <Progress value={r.percentual} className="h-2" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Aba Evolução de Vendas ────────────────────────────────────────────────────

function TabEvolucao() {
  const [dtInicio, setDtInicio] = useState(anoInicio());
  const [dtFim,    setDtFim]    = useState(today());

  const { data, isLoading } = useEvolucaoVendas({ dtInicio, dtFim });
  const maxLiquido = Math.max(...(data?.map((x) => x.totalLiquido) ?? [1]));
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} onChangeDtInicio={setDtInicio} onChangeDtFim={setDtFim} />
        <BotaoExportar endpoint="/api/relatorios/vendas/evolucao/exportar" params={exportParams} />
      </div>
      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : !data?.length ? (
        <p className="text-muted-foreground text-center py-8">Nenhum dado no período.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mês/Ano</TableHead>
                <TableHead className="text-right">Qtd Docs</TableHead>
                <TableHead className="text-right">Total Bruto</TableHead>
                <TableHead className="text-right">Total Líquido</TableHead>
                <TableHead className="text-right">Ticket Médio</TableHead>
                <TableHead className="w-40">Proporção</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((r) => (
                <TableRow key={`${r.ano}-${r.mes}`}>
                  <TableCell className="font-medium">{r.mesDescricao}/{r.ano}</TableCell>
                  <TableCell className="text-right">{r.qtdDocumentos}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCur(r.totalBruto)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalLiquido)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCur(r.ticketMedio)}</TableCell>
                  <TableCell>
                    <Progress value={maxLiquido > 0 ? (r.totalLiquido / maxLiquido) * 100 : 0} className="h-2" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

const RelatoriosVendas = () => (
  <div className="p-6 space-y-6">
    <div>
      <h1 className="text-3xl font-bold text-foreground">Relatórios de Vendas</h1>
      <p className="text-muted-foreground mt-1">Análise de vendas por cliente, produto, forma de pagamento e evolução.</p>
    </div>
    <Tabs defaultValue="clientes">
      <TabsList className="flex-wrap">
        <TabsTrigger value="clientes"   className="gap-2"><Users      className="h-4 w-4" />Por Cliente</TabsTrigger>
        <TabsTrigger value="produtos"   className="gap-2"><Package    className="h-4 w-4" />Por Produto</TabsTrigger>
        <TabsTrigger value="pagamento"  className="gap-2"><CreditCard className="h-4 w-4" />Forma de Pagamento</TabsTrigger>
        <TabsTrigger value="evolucao"   className="gap-2"><TrendingUp className="h-4 w-4" />Evolução Mensal</TabsTrigger>
      </TabsList>
      <TabsContent value="clientes">  <Card><CardHeader><CardTitle>Vendas por Cliente</CardTitle></CardHeader><CardContent><TabClientes /></CardContent></Card></TabsContent>
      <TabsContent value="produtos">  <Card><CardHeader><CardTitle>Vendas por Produto</CardTitle></CardHeader><CardContent><TabProdutos /></CardContent></Card></TabsContent>
      <TabsContent value="pagamento"> <Card><CardHeader><CardTitle>Vendas por Forma de Pagamento</CardTitle></CardHeader><CardContent><TabFormasPagamento /></CardContent></Card></TabsContent>
      <TabsContent value="evolucao">  <Card><CardHeader><CardTitle>Evolução Mensal de Vendas</CardTitle></CardHeader><CardContent><TabEvolucao /></CardContent></Card></TabsContent>
    </Tabs>
  </div>
);

export default RelatoriosVendas;
